import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { extractHandoffToken } from "../lib/handoff";
import { kioskClient, KioskApiError } from "../lib/kiosk-client";
import type { BootstrapPayload, Order } from "../lib/kiosk-client";
import { FlowPage } from "./FlowPage";
import { BigButton } from "../components/BigButton";

interface Props {
  bootstrap: BootstrapPayload;
  onDone: () => void;
}

export function HandoffPage({ bootstrap, onDone }: Props) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [order, setOrder] = useState<Order | null>(null);
  const [amountDue, setAmountDue] = useState(0);

  useEffect(() => {
    const tok = extractHandoffToken(params.toString());
    if (!tok) {
      setError(t("handoff.errorInvalid"));
      setLoading(false);
      return;
    }

    kioskClient
      .claimHandoff(tok)
      .then(({ data }) => {
        setOrder(data.order);
        setAmountDue(data.amount_due_cents);
      })
      .catch((err) => {
        if (err instanceof KioskApiError && err.status === 401) {
          setError(t("handoff.errorInvalid"));
        } else {
          setError(t("handoff.errorGeneric"));
        }
      })
      .finally(() => setLoading(false));
  }, [params, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-2xl text-[var(--kiosk-brand-muted)]">
          {t("handoff.loading")}
        </p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex flex-col items-center justify-center gap-8 h-full p-12">
        <p role="alert" className="text-red-600 text-2xl text-center">
          {error}
        </p>
        <BigButton variant="secondary" onClick={() => navigate("/")}>
          {t("flow.cancel")}
        </BigButton>
      </div>
    );
  }

  return (
    <FlowPage
      bootstrap={bootstrap}
      initialState={{
        order_id: order.id,
        amount_cents: amountDue,
      }}
      onDone={onDone}
    />
  );
}
